/**
 * Hook pour extraire les informations de paiement via OCR
 * Utilise l'Edge Function ocr-claude-vision avec documentType='justificatif_paiement'
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { captureError } from '@/lib/sentry';
import { calculateFileHash } from '@/lib/security/fileValidator';
import type { PaiementExtractionData, MoyenPaiement } from '@/types/paiement';


/**
 * Résultat de l'extraction OCR d'un justificatif de paiement
 */
export interface PaiementOCRResult {
  success: boolean;
  data?: PaiementExtractionData;
  confidence: number;
  error?: string;
  message?: string;
  fileUrl?: string;
}

/**
 * État du hook usePaiementOCR
 */
export interface UsePaiementOCRState {
  isExtracting: boolean;
  extractedData: PaiementExtractionData | null;
  confidence: number;
  fileUrl: string | null;
  error: string | null;
}

/**
 * Actions du hook usePaiementOCR
 */
export interface UsePaiementOCRActions {
  extractPaiement: (file: File) => Promise<PaiementOCRResult>;
  reset: () => void;
  mapToMoyenPaiement: (typeDetecte: string | undefined) => MoyenPaiement | undefined;
}

/**
 * Retour complet du hook usePaiementOCR
 */
export type UsePaiementOCRReturn = UsePaiementOCRState & UsePaiementOCRActions;


/**
 * Hook pour extraire les informations d'un justificatif de paiement
 */
export function usePaiementOCR(): UsePaiementOCRReturn {
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState<PaiementExtractionData | null>(null);
  const [confidence, setConfidence] = useState(0);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Réinitialiser l'état
   */
  const reset = useCallback(() => {
    setIsExtracting(false);
    setExtractedData(null);
    setConfidence(0);
    setFileUrl(null);
    setError(null);
  }, []);

  /**
   * Mapper le type de paiement détecté vers MoyenPaiement
   */
  const mapToMoyenPaiement = useCallback((
    typeDetecte: string | undefined
  ): MoyenPaiement | undefined => {
    if (!typeDetecte) return undefined;

    const mapping: Record<string, MoyenPaiement> = {
      'virement': 'virement',
      'cheque': 'cheque',
      'chèque': 'cheque',
      'carte': 'carte',
      'cb': 'carte',
      'prelevement': 'prelevement',
      'prélèvement': 'prelevement',
      'especes': 'especes',
      'espèces': 'especes',
    };

    return mapping[typeDetecte.toLowerCase()];
  }, []);

  /**
   * Extraire les informations d'un fichier (PDF ou image)
   */
  const extractPaiement = useCallback(async (file: File): Promise<PaiementOCRResult> => {
    setIsExtracting(true);
    setError(null);

    try {
      // 1. Vérifier l'authentification
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        const errMsg = 'Vous devez être connecté pour utiliser le Scan IA';
        setError(errMsg);
        toast.error(errMsg);
        return { success: false, confidence: 0, error: errMsg };
      }

      // 2. Récupérer l'entreprise
      const { data: entreprise, error: entrepriseError } = await supabase
        .from('entreprises')
        .select('id')
        .eq('proprietaire_user_id', user.id)
        .single();

      if (entrepriseError || !entreprise) {
        const errMsg = 'Impossible de trouver votre entreprise';
        setError(errMsg);
        toast.error(errMsg);
        return { success: false, confidence: 0, error: errMsg };
      }

      // 3. Vérifier la taille du fichier (20 Mo max)
      if (file.size > 20 * 1024 * 1024) {
        const errMsg = 'Fichier trop volumineux (limite: 20 Mo)';
        setError(errMsg);
        toast.error(errMsg);
        return { success: false, confidence: 0, error: errMsg };
      }

      // 4. Vérifier le type de fichier
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        const errMsg = 'Format non supporté. Utilisez PDF, JPG ou PNG.';
        setError(errMsg);
        toast.error(errMsg);
        return { success: false, confidence: 0, error: errMsg };
      }

      // 4b. CACHE OCR - Vérifier si ce fichier a déjà été analysé
      const fileHash = await calculateFileHash(file);
      if (import.meta.env.DEV) console.log('[PAIEMENT-OCR] Hash fichier:', fileHash.substring(0, 16) + '...');

      const { data: cached, error: cacheError } = await supabase
        .from('ocr_cache')
        .select('ocr_result, confidence')
        .eq('entreprise_id', entreprise.id)
        .eq('file_hash', fileHash)
        .maybeSingle();

      if (cached && !cacheError) {
        if (import.meta.env.DEV) console.log('[PAIEMENT-OCR] ✅ Cache hit - économie API');

        // Mettre à jour last_used_at
        await supabase
          .from('ocr_cache')
          .update({ last_used_at: new Date().toISOString() })
          .eq('entreprise_id', entreprise.id)
          .eq('file_hash', fileHash);

        const cachedResponse = cached.ocr_result as any;

        if (cachedResponse?.success && cachedResponse?.data) {
          const extractedPaymentData: PaiementExtractionData = {
            montant: cachedResponse.data?.montant || undefined,
            date_paiement: cachedResponse.data?.date_paiement || undefined,
            reference: cachedResponse.data?.reference || undefined,
            payeur_nom: cachedResponse.data?.payeur_nom || undefined,
            type_paiement_detecte: cachedResponse.data?.type_paiement_detecte || undefined,
            iban_source: cachedResponse.data?.iban_source || undefined,
            iban_destination: cachedResponse.data?.iban_destination || undefined,
            libelle: cachedResponse.data?.libelle || undefined,
          };

          setExtractedData(extractedPaymentData);
          setConfidence(cachedResponse.confidence || cached.confidence || 0);
          toast.success('Document déjà analysé (cache)');

          return {
            success: true,
            data: extractedPaymentData,
            confidence: cachedResponse.confidence || cached.confidence || 0,
            message: 'Résultat depuis le cache',
          };
        }
      }

      if (import.meta.env.DEV) console.log('[PAIEMENT-OCR] Cache miss - appel API');

      // 5. Upload temporaire dans le bucket "factures" (pour les justificatifs)
      const tempPath = `${entreprise.id}/paiements/temp-${Date.now()}-${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('factures')
        .upload(tempPath, file);

      if (uploadError) {
        console.error('Erreur upload:', uploadError);
        const errMsg = 'Erreur lors de l\'upload du fichier';
        setError(errMsg);
        toast.error(errMsg);
        return { success: false, confidence: 0, error: errMsg };
      }

      // 6. Générer URL signée (1 heure)
      const { data: urlData } = await supabase.storage
        .from('factures')
        .createSignedUrl(tempPath, 3600);

      if (!urlData?.signedUrl) {
        const errMsg = 'Impossible de générer l\'URL signée';
        setError(errMsg);
        toast.error(errMsg);
        return { success: false, confidence: 0, error: errMsg };
      }

      setFileUrl(urlData.signedUrl);

      // 7. Appeler l'Edge Function OCR
      toast.info('Extraction en cours...', { duration: 2000 });

      if (import.meta.env.DEV) console.log('[PAIEMENT-OCR] Calling ocr-claude-vision with:', {
        fileUrl: urlData.signedUrl.substring(0, 100) + '...',
        documentType: 'justificatif_paiement',
        entrepriseId: entreprise.id
      });

      const { data: ocrResponse, error: ocrError } = await supabase.functions.invoke(
        'ocr-claude-vision',
        {
          body: {
            fileUrl: urlData.signedUrl,
            documentType: 'justificatif_paiement',
            entrepriseId: entreprise.id
          }
        }
      );

      if (import.meta.env.DEV) console.log('[PAIEMENT-OCR] Response:', { ocrResponse, ocrError });

      if (ocrError) {
        console.error('Erreur OCR:', ocrError);
        const errMsg = 'Erreur lors de l\'extraction';
        setError(errMsg);
        toast.error(errMsg);
        return { success: false, confidence: 0, error: errMsg };
      }

      // 8. Traiter la réponse
      if (!ocrResponse || !ocrResponse.success) {
        const errMsg = ocrResponse?.message || 'Extraction échouée';
        setError(errMsg);
        toast.error(errMsg);
        return {
          success: false,
          confidence: ocrResponse?.confidence || 0,
          error: errMsg,
          message: ocrResponse?.message
        };
      }

      // 9. Stocker les données extraites
      const extractedPaymentData: PaiementExtractionData = {
        montant: ocrResponse.data?.montant || undefined,
        date_paiement: ocrResponse.data?.date_paiement || undefined,
        reference: ocrResponse.data?.reference || undefined,
        payeur_nom: ocrResponse.data?.payeur_nom || undefined,
        type_paiement_detecte: ocrResponse.data?.type_paiement_detecte || undefined,
        iban_source: ocrResponse.data?.iban_source || undefined,
        iban_destination: ocrResponse.data?.iban_destination || undefined,
        libelle: ocrResponse.data?.libelle || undefined,
      };

      setExtractedData(extractedPaymentData);
      setConfidence(ocrResponse.confidence || 0);

      // Stocker dans le cache OCR (non bloquant)
      try {
        await supabase
          .from('ocr_cache')
          .upsert({
            entreprise_id: entreprise.id,
            file_hash: fileHash,
            file_name: file.name,
            file_size: file.size,
            document_type: 'justificatif_paiement',
            ocr_result: ocrResponse,
            confidence: ocrResponse.confidence,
          }, { onConflict: 'entreprise_id,file_hash' });
        if (import.meta.env.DEV) console.log('[PAIEMENT-OCR] Résultat stocké dans le cache');
      } catch (cacheInsertError) {
        if (import.meta.env.DEV) console.warn('[PAIEMENT-OCR] Erreur stockage cache (non bloquant):', cacheInsertError);
      }

      const confidenceMsg = ocrResponse.confidence >= 0.8
        ? 'Extraction réussie'
        : 'Extraction partielle - vérifiez les données';

      toast.success(confidenceMsg);

      return {
        success: true,
        data: extractedPaymentData,
        confidence: ocrResponse.confidence || 0,
        message: ocrResponse.message,
        fileUrl: urlData.signedUrl
      };

    } catch (err) {
      console.error('Erreur extractPaiement:', err);
      const error = err instanceof Error ? err : new Error(String(err));
      captureError(error, {
        context: 'paiement_ocr_extraction',
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      const errMsg = err instanceof Error ? err.message : 'Erreur inattendue';
      setError(errMsg);
      toast.error(errMsg);
      return { success: false, confidence: 0, error: errMsg };
    } finally {
      setIsExtracting(false);
    }
  }, []);

  return {
    // État
    isExtracting,
    extractedData,
    confidence,
    fileUrl,
    error,
    // Actions
    extractPaiement,
    reset,
    mapToMoyenPaiement,
  };
}
